import type { InferenceModelType } from "@kuzco/models";
import { INFERENCE_MODEL_METADATA } from "@kuzco/models/models-metadata";
import { INFERENCE_MODELS } from "@kuzco/models/inference-models";
import { isEmbeddingModel } from "@kuzco/models/embedding-model";
import { SYSTEM_STATUS_ID } from "@kuzco/models/system-status";

import { TEAM_TIMEOUT_OVERRIDES_IN_MS } from "../constants/TeamTimeoutOverrides";
import { TEAM_TIME_TO_FIRST_TOKEN_TIMEOUT_OVERRIDES_IN_MS } from "../constants/TeamTimeToFirstTokenTimeoutOverrides";
import { PRIVATE_MODELS_APPROVED_SET } from "../pricing/ModelPricing";
import { time } from "../utils/time";

const E2E_TEST_SUPPORTED_MODELS = new Set<InferenceModelType>([
  "meta-llama/llama-3.1-8b-instruct/q4-k-m",
  "meta-llama/llama-3.2-1b-instruct/fp-8",
  "meta-llama/llama-3.2-1b-instruct/fp-16",
]);

function areE2ETestModelsEnabled(): boolean {
  return process.env.ENABLE_E2E_TEST_MODELS === "true";
}

function getModelMetadata(model: InferenceModelType) {
  const metadata = INFERENCE_MODEL_METADATA[model];
  if (!metadata) {
    throw new Error(`No metadata found for model: ${model}`);
  }
  return metadata;
}

function isJsonObjectModeSupported(model: InferenceModelType) {
  return getModelMetadata(model).response_format_json_object;
}

function isStructuredOutputsSupported(model: InferenceModelType) {
  return getModelMetadata(model).structured_outputs;
}

function isImageInputSupported(model: InferenceModelType) {
  return getModelMetadata(model).image_input;
}

function isToolUseSupported(model: InferenceModelType) {
  return getModelMetadata(model).tools;
}

function getContextSizePerModel(model: InferenceModelType) {
  return getModelMetadata(model).context_size;
}

function getMaxOutputTokenLength(model: InferenceModelType) {
  return getModelMetadata(model).max_output_tokens;
}

function isModelSupported(model: InferenceModelType): boolean {
  if (areE2ETestModelsEnabled() && E2E_TEST_SUPPORTED_MODELS.has(model)) {
    return true;
  }

  const metadata = INFERENCE_MODEL_METADATA[model];
  if (metadata) {
    return metadata.is_model_supported;
  } else {
    return false;
  }
}

function isModelAccessibleByTeam(
  model: InferenceModelType,
  teamId: string,
): boolean {
  if (PRIVATE_MODELS_APPROVED_SET[model]) {
    return (
      PRIVATE_MODELS_APPROVED_SET[model].has(teamId) ||
      teamId === SYSTEM_STATUS_ID
    );
  }

  return true;
}

function isModelPublic(model: InferenceModelType): boolean {
  return getModelMetadata(model).is_model_public;
}

function isShownOnPlayground(model: InferenceModelType): boolean {
  const metadata = getModelMetadata(model);
  return metadata.is_model_public || metadata.is_shown_on_playground === true;
}

function isReasoningModel(model: InferenceModelType) {
  return getModelMetadata(model).supports_reasoning;
}

function isModelOffline(model: InferenceModelType) {
  return getModelMetadata(model).offline;
}

function isInferenceNetCustomModel(model: InferenceModelType) {
  return getModelMetadata(model).maker === "InferenceNet";
}

function getQuantizationForModel(model: InferenceModelType): string {
  return getModelMetadata(model).quantization;
}

function getHuggingFaceModelIdentifier(
  model: InferenceModelType,
): string | undefined {
  return getModelMetadata(model).hugging_face_identifier;
}

const PUBLIC_MODELS =
  INFERENCE_MODELS.filter(isModelSupported).filter(isModelPublic);

const PLAYGROUND_MODELS =
  INFERENCE_MODELS.filter(isModelSupported).filter(isShownOnPlayground);

const PUBLIC_SUPPORTED_MODELS = PUBLIC_MODELS.filter(isModelSupported);

const SUPPORTED_MODELS = INFERENCE_MODELS.filter(isModelSupported);

function getModelNotSupportedErrorMessage(
  requestedModel: string,
): string | null {
  const model = requestedModel;

  if (!(INFERENCE_MODELS as readonly string[]).includes(model)) {
    return `Model '${model}' is not supported - please contact us to request support for this model.`;
  }

  if (!isModelSupported(model)) {
    return `Model '${model}' is not supported - please contact us to request support for this model.`;
  }

  return null;
}

function getInferenceTimeoutInMsForModel(
  model: InferenceModelType,
  teamId?: string,
): number | null {
  if (teamId && teamId in TEAM_TIMEOUT_OVERRIDES_IN_MS) {
    return TEAM_TIMEOUT_OVERRIDES_IN_MS[teamId] ?? null;
  }

  switch (model) {
    case "deepseek/deepseek-v3/fp-8":
    case "deepseek/deepseek-v3-0324/fp-8":
    case "deepseek/deepseek-v4-flash":
    case "deepseek/deepseek-r1/fp-8":
    case "deepseek/deepseek-r1-0528/fp-8":
      return time.minutesToMilliseconds(5);

    case "nous/hermes-3-llama-3.1-405b/fp-8":
    case "deepseek/r1-distill-llama-70b/fp-8":
    case "google/gemma-3-27b-instruct/bf-16":
    case "qwen/qwq-32b/fp-8":
      return time.minutesToMilliseconds(3);

    default:
      return null;
  }
}

function getInferenceTimeToFirstTokenTimeoutInMsForModel({
  modelId,
  teamId,
}: {
  modelId: InferenceModelType;
  teamId: string;
}): number | null {
  if (isEmbeddingModel(modelId)) {
    return time.minutesToMilliseconds(1);
  }

  if (teamId && teamId in TEAM_TIME_TO_FIRST_TOKEN_TIMEOUT_OVERRIDES_IN_MS) {
    return TEAM_TIME_TO_FIRST_TOKEN_TIMEOUT_OVERRIDES_IN_MS[teamId] ?? null;
  }

  // Cold million-token prefill takes about 110s on B300, beyond the 60s default
  // (Kimi K3, and Kimi K3.1 Pro on the same architecture and 1M context).
  // Hy4 preview OpenRouter traffic carries 130k-850k token prompts that hit the
  // 60s budget mid-prefill and count against provider uptime. Step-5-Preview
  // takes ~33s for a cold 1M-token prefill on B300 TP4 before any queueing.
  // MiniMax M3.1 Flash serves a 1M-token context. GLM-5.2 prompts of
  // ~600K+ tokens need >60s at ~10.6K tok/s, and requests queued behind them wait too.
  // GLM-5.3-Flash tool sessions of 80-140K tokens wait behind other cold prefills
  // when the KV pool is full; the SGLang queue wait counts against this budget.
  if (
    modelId === "moonshotai/kimi-k3" ||
    modelId === "moonshotai/kimi-k3.1-pro" ||
    modelId === "kimi-k3.1-pro" ||
    modelId === "glm-5.3-flash" ||
    modelId === "z-ai/glm-5.3-flash" ||
    modelId === "zai-org/GLM-5.3-Flash" ||
    modelId === "glm-5.2" ||
    modelId === "z-ai/glm-5.2" ||
    modelId === "zai-org/GLM-5.2" ||
    modelId === "glm-5.3" ||
    modelId === "z-ai/glm-5.3" ||
    modelId === "zai-org/GLM-5.3" ||
    modelId === "tencent/hy4-preview" ||
    modelId === "hy4-preview" ||
    modelId === "stepfun/step-5-preview" ||
    modelId === "step-5-preview" ||
    modelId === "minimax/minimax-m3.1-flash" ||
    modelId === "minimax-m3.1-flash"
  ) {
    return time.minutesToMilliseconds(3);
  }

  // DeepSeek V4.1 Flash on OpenRouter: cache-heavy agent traffic whose largest
  // prompts are 0.7-1.5 MB (~175-375K tokens). Under peak load, an uncached
  // prefill of that size (4-5K tok/s) waits behind other prefills on the same
  // worker, and the 60s default returns a 500 for a request that would finish
  // (~0.2-0.5% of requests at peak). 2 minutes covers those prefills.
  if (
    modelId === "deepseek-v4.1-flash" ||
    modelId === "deepseek/deepseek-v4.1-flash" ||
    modelId === "deepseek-ai/DeepSeek-V4.1-Flash" ||
    modelId === "deepseek-v4.1-flash-1017" ||
    modelId === "deepseek/deepseek-v4.1-flash-1017" ||
    modelId === "deepseek-ai/DeepSeek-V4.1-Flash-1017" ||
    // V4.1 Pro serves the same agent traffic on a larger model, so its
    // prefills are at least as slow.
    modelId === "deepseek-v4.1-pro" ||
    modelId === "deepseek/deepseek-v4.1-pro"
  ) {
    return time.minutesToMilliseconds(2);
  }

  // schematron-v2-small serves very large, prefill-heavy prompts (p50 ~24k,
  // p90 ~54k tokens). Under load, requests legitimately sit in the elixir/vLLM
  // queue and grind through prefill for longer than the 60s global budget, so
  // the global TTFT timeout fails requests that would otherwise succeed. Allow
  // up to 120s before first token for this model.
  if (modelId === "inference-net/schematron-v2-small") {
    return time.minutesToMilliseconds(2);
  }

  // schematron-v2-turbo can wait in the elixir engine queue (up to its queue timeout)
  // (set 2026-06-11 alongside the 192→116 parallelism cut), and the entire
  // queue wait counts against TTFT: the elixir SSE generator is lazy, so the
  // TTFT timer starts before the first request byte is even sent. With the
  // global 60s budget, requests get aborted here while still sitting in a
  // queue that would have served them. Budget = 120s queue + dispatch +
  // prefill headroom (turbo serves the same large schematron prompts).
  if (modelId === "inference-net/schematron-v2-turbo") {
    return time.minutesToMilliseconds(3);
  }

  return null;
}

function huggingFaceIdentifierToFilename(
  model: string,
  type: "README" | "SUMMARY",
): string {
  const modelString = model.replace(/\//g, "_").replace(/\./g, "_");
  return `${modelString}-${type}.json`;
}

export const ModelUtils = {
  getContextSizePerModel,
  getHuggingFaceModelIdentifier,
  getInferenceTimeoutInMsForModel,
  getInferenceTimeToFirstTokenTimeoutInMsForModel,
  getMaxOutputTokenLength,
  getModelNotSupportedErrorMessage,
  getQuantizationForModel,
  huggingFaceIdentifierToFilename,
  isImageInputSupported,
  isJsonObjectModeSupported,
  isModelPublic,
  isModelSupported,
  isModelOffline,
  isReasoningModel,
  isStructuredOutputsSupported,
  isToolUseSupported,
  PLAYGROUND_MODELS,
  PUBLIC_MODELS,
  PUBLIC_SUPPORTED_MODELS,
  SUPPORTED_MODELS,
  isModelAccessibleByTeam,
  isInferenceNetCustomModel,
};
